import React, { useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SafeIcon from '../../common/SafeIcon';
import { getHistory } from '../../utils/history';
import { classifyStatus } from '../../utils/checker';
import { useTheme } from '../../contexts/ThemeContext';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart3 } = FiIcons;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const GraphTab = () => {
  const { isDark } = useTheme();
  const history = getHistory();

  const { responseTimeData, statusData } = useMemo(() => {
    if (history.length === 0) {
      return { responseTimeData: null, statusData: null };
    }

    // Sort by timestamp (newest first, then reverse for chart)
    const sortedHistory = [...history].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    // Response time chart data
    const responseTimeData = {
      labels: sortedHistory.map((item, index) => `Check ${index + 1}`),
      datasets: [
        {
          label: 'Response Time (ms)',
          data: sortedHistory.map(item => item.responseTimeMs),
          borderColor: isDark ? '#60a5fa' : '#3b82f6',
          backgroundColor: isDark ? '#60a5fa20' : '#3b82f620',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    // Status distribution data
    const statusCounts = { Up: 0, Degraded: 0, Down: 0 };
    sortedHistory.forEach(item => {
      const status = classifyStatus(item);
      statusCounts[status]++;
    });

    const statusData = {
      labels: ['Up', 'Degraded', 'Down'],
      datasets: [
        {
          label: 'Status Count',
          data: [statusCounts.Up, statusCounts.Degraded, statusCounts.Down],
          backgroundColor: [
            '#10b981',
            '#f59e0b',
            '#ef4444',
          ],
          borderColor: [
            '#059669',
            '#d97706',
            '#dc2626',
          ],
          borderWidth: 1,
        },
      ],
    };

    return { responseTimeData, statusData };
  }, [history, isDark]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: isDark ? '#e5e7eb' : '#374151',
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        titleColor: isDark ? '#e5e7eb' : '#374151',
        bodyColor: isDark ? '#e5e7eb' : '#374151',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: isDark ? '#374151' : '#e5e7eb',
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          font: {
            size: 11,
          },
        },
      },
      y: {
        grid: {
          color: isDark ? '#374151' : '#e5e7eb',
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          font: {
            size: 11,
          },
        },
      },
    },
  };

  const barOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        beginAtZero: true,
        ticks: {
          ...chartOptions.scales.y.ticks,
          stepSize: 1,
        },
      },
    },
  };

  if (history.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 text-gray-500 dark:text-gray-400">
        <SafeIcon icon={FiBarChart3} className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-4 opacity-50" />
        <p className="text-base sm:text-lg font-medium mb-2">No data to visualize</p>
        <p className="text-xs sm:text-sm">Check some websites to see response time trends and status distribution</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Response Time Trends
        </h3>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
          <div className="h-64 sm:h-80">
            <Line data={responseTimeData} options={chartOptions} />
          </div>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-2">
          Shows response times for your last {history.length} checks across all domains
        </p>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Status Distribution
        </h3>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
          <div className="h-64 sm:h-80 max-w-md mx-auto">
            <Bar data={statusData} options={barOptions} />
          </div>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-2">
          Distribution of status results across your last {history.length} checks
        </p>
      </div>

      {history.length < 10 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-400 leading-relaxed">
            <span className="font-medium">Tip:</span> Check more websites to see richer data visualization. 
            History tracks your last 500 checks.
          </p>
        </div>
      )}
    </div>
  );
};

export default GraphTab;